// Git 数据 API(浏览器 fetch 视角)
//
// 本文件所有 JSON 端点都走 /api/v1/gitdata/... (后端 git_api.rs),由 axios
// 自动携带 Bearer token;不会触发 git CLI 专用的 Basic Auth 弹窗。
//
// 真正的 Git Smart HTTP 端点(/api/v1/git/...git/info/refs 等)只给 git CLI
// 使用,浏览器前端不应该直接请求。
import { apiClient } from './client';
import type { GitRef } from './types';

const REFS_URL = (owner: string, name: string) =>
  `/v1/gitdata/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/refs`;

const TREE_URL = (owner: string, name: string, ref: string) =>
  `/v1/gitdata/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/tree/${encodeURIComponent(ref)}`;

const BLOB_URL = (owner: string, name: string, ref: string, path: string) =>
  `/v1/gitdata/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/blob/${encodeURIComponent(ref)}/${path}`;

const COMMITS_URL = (owner: string, name: string, ref: string, limit: number) =>
  `/v1/gitdata/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/commits/${encodeURIComponent(ref)}?limit=${limit}`;

const COMMIT_FILES_URL = (owner: string, name: string, oid: string) =>
  `/v1/gitdata/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/commits/${encodeURIComponent(oid)}/files`;

const COMMIT_FILE_DIFF_URL = (owner: string, name: string, oid: string, path: string) =>
  `/v1/gitdata/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/commits/${encodeURIComponent(oid)}/file?path=${encodeURIComponent(path)}`;

const enc = encodeURIComponent;

const BRANCHES_URL = (owner: string, name: string) =>
  `/v1/gitdata/${enc(owner)}/${enc(name)}/branches`;

export interface RefsResponse {
  refs: GitRef[];
}

export interface TreeEntry {
  name: string;
  path: string;
  /** "blob" / "tree" */
  kind: string;
  oid: string;
  size?: number;
  mode: string;
  /** 该路径在此 ref 下最近一次提交的标题(文件树右侧展示用),无提交时省略 */
  last_commit?: string | null;
}

export interface BlobMeta {
  oid: string;
  size: number;
  ref_name: string;
  path: string;
  text: string | null;
  mime: string;
  is_binary: boolean;
}

export interface CommitSummary {
  oid: string;
  short_oid: string;
  author_name: string;
  author_email: string;
  authored_at: string;
  committer_name: string;
  committer_email: string;
  committed_at: string;
  message: string;
  subject: string;
  parents: string[];
}

export interface TreeResponse {
  entries: TreeEntry[];
  ref_name: string;
  path: string;
}

export interface CommitsResponse {
  commits: CommitSummary[];
  ref_name: string;
  total: number;
}

/** commit 中发生变化的一个文件 */
export interface CommitFile {
  /** 相对 repo 根的路径(重命名时为新路径) */
  path: string;
  /** A(新增) / M(修改) / D(删除) / R(重命名) */
  status: string;
  old_path?: string | null;
  additions: number;
  deletions: number;
  binary: boolean;
}

export interface CommitFilesResponse {
  oid: string;
  files: CommitFile[];
}

/** commit 中单个文件的两版本内容,给 diff 左右分栏展示 */
export interface CommitFileDiff {
  path: string;
  status: string;
  binary: boolean;
  /** 父提交中的内容(新增文件为空) */
  original: string;
  /** 当前提交中的内容(删除文件为空) */
  modified: string;
}

export interface SchemeInfo {
  enabled: boolean;
  port: number;
}

export interface ServerInfo {
  /** 对外仓库服务器域名/IP(系统设置里配置,可空)。空时 cloneUrl 用 window.location.hostname 兜底 */
  host?: string | null;
  http: SchemeInfo;
  https: SchemeInfo;
}

export interface CreateBranchBody {
  /** 新分支名 */
  name: string;
  /** 基于哪个 ref(branch / tag / commit)创建,可选;为空则基于 HEAD */
  from?: string;
}

export interface RenameBranchBody {
  new_name: string;
}

export const gitApi = {
  /** 拉取 ref 列表(分支 + 标签),走 JSON 端点,自动携带 Bearer token */
  async listRefs(owner: string, name: string): Promise<GitRef[]> {
    const { data } = await apiClient.get<RefsResponse>(REFS_URL(owner, name));
    return data.refs;
  },

  /** 读 tree(JSON,需要 Bearer token) */
  async readTree(
    owner: string,
    name: string,
    ref: string,
  ): Promise<TreeResponse> {
    const { data } = await apiClient.get<TreeResponse>(TREE_URL(owner, name, ref));
    return data;
  },

  /** 读 blob meta(JSON) */
  async readBlobMeta(
    owner: string,
    name: string,
    ref: string,
    path: string,
  ): Promise<BlobMeta> {
    const { data } = await apiClient.get<BlobMeta>(BLOB_URL(owner, name, ref, path));
    return data;
  },

  /** 列 commit 历史(JSON) */
  async listCommits(
    owner: string,
    name: string,
    ref: string,
    limit: number = 30,
  ): Promise<CommitsResponse> {
    const { data } = await apiClient.get<CommitsResponse>(COMMITS_URL(owner, name, ref, limit));
    return data;
  },

  /** 列某 commit 变化的文件(commit detail 左侧文件列表) */
  async listCommitFiles(
    owner: string,
    name: string,
    oid: string,
  ): Promise<CommitFilesResponse> {
    const { data } = await apiClient.get<CommitFilesResponse>(COMMIT_FILES_URL(owner, name, oid));
    return data;
  },

  /** 取某 commit 中单个文件的 diff(original + modified) */
  async getCommitFileDiff(
    owner: string,
    name: string,
    oid: string,
    path: string,
  ): Promise<CommitFileDiff> {
    const { data } = await apiClient.get<CommitFileDiff>(
      COMMIT_FILE_DIFF_URL(owner, name, oid, path),
    );
    return data;
  },

  /** 拉取服务端协议/端口信息(前端 clone URL 切换器用) */
  async serverInfo(): Promise<ServerInfo> {
    const { data } = await apiClient.get<ServerInfo>('/v1/server/info');
    return data;
  },

  /**
   * clone URL(给用户在终端用 git CLI,需要 .git 后缀)。
   * @param scheme 'http' | 'https'
   * @param owner  仓库 owner
   * @param name   仓库名
   * @param port   对外端口(来自 serverInfo,可配置 / 已考虑 Docker-NAT 映射)
   * @param host   对外域名/IP(来自 serverInfo.host);为空时用浏览器访问到的 hostname 兜底
   *
   * host 优先取系统设置里的「对外仓库地址」,保证 clone 直连 gitman 自身端口、不经网关;
   * 未配置时才降级为当前页面 hostname。
   */
  cloneUrl(
    scheme: 'http' | 'https',
    owner: string,
    name: string,
    port: number,
    host?: string | null,
  ): string {
    const h = host?.trim() || window.location.hostname;
    // 默认端口(http 80 / https 443)省略,避免 URL 出现多余的 :80 / :443。
    const isDefaultPort =
      (scheme === 'http' && port === 80) || (scheme === 'https' && port === 443);
    const authority = isDefaultPort ? h : `${h}:${port}`;
    return `${scheme}://${authority}/api/v1/git/${encodeURIComponent(owner)}/${encodeURIComponent(name)}.git`;
  },

  /** 创建分支(POST /gitdata/.../branches) */
  async createBranch(
    owner: string,
    name: string,
    body: CreateBranchBody,
  ): Promise<GitRef> {
    const { data } = await apiClient.post<GitRef>(BRANCHES_URL(owner, name), body);
    return data;
  },

  /** 删除分支(DELETE /gitdata/.../branches/{branch}) */
  async deleteBranch(owner: string, name: string, branch: string): Promise<void> {
    await apiClient.delete(`${BRANCHES_URL(owner, name)}/${enc(branch)}`);
  },

  /** 重命名分支(PATCH /gitdata/.../branches/{branch}) */
  async renameBranch(
    owner: string,
    name: string,
    branch: string,
    newName: string,
  ): Promise<void> {
    await apiClient.patch(`${BRANCHES_URL(owner, name)}/${enc(branch)}`, {
      new_name: newName,
    });
  },
};

